import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./App.css";
import MainContent from "./components/MainContent";
import SettingsDrawer from "./components/SettingsDrawer";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AccessibilityPolicy from "./components/AccessibilityPolicy";
import TermsOfUse from "./components/TermsOfUse";
import Roadmap from "./components/Roadmap";
import Documentation from "./components/Documentation";
import AboutUs from "./components/AboutUs";
import ContactUs from "./components/ContactUs";
import FAQ from "./components/FAQ";
import { useTaskManager } from "./hooks/useTaskManager";
import { useSettings, LAST_SAVED_SNAPSHOT_KEY } from "./hooks/useSettings";
import { useTodoLists } from "./hooks/useTodoLists";
import { useDataImport } from "./hooks/useDataImport";
import { useDataChangeDetector } from "./hooks/useDataChangeDetector";

// NEW: Import for sample tasks
import { SAMPLE_TASK_TEMPLATES } from "./sampleData"; // Adjust path if your sampleData.js is elsewhere
import { createTask } from "./utils/taskUtils"; // Adjust path if your taskUtils.js is elsewhere

const MainApplicationUI = ({
  tasks,
  dailyTasks,
  addTask,
  updateTask,
  deleteTask,
  taskDispatch,
  addDailyTask,
  settings,
  toggleSetting,
  updateSetting,
  deleteTag,
  onUpdateTasksWithDeletedTag,
  onCleanUpCompletedTasks,
  onSaveAllAndRefresh,
  hasUnsavedChanges,
  importDataHook,
  todoLists,
  todoDispatch,
  showModal,
  setShowModal,
  handleWipeAll,
  isDrawerOpen,
  setIsDrawerOpen,
  onAddSampleTasks, // NEW: Prop for adding sample tasks
}) => {
  return (
    <div className="app">
      <SettingsDrawer
        settings={settings}
        toggleSetting={toggleSetting}
        updateSetting={updateSetting}
        deleteTag={deleteTag}
        onUpdateTasksWithDeletedTag={onUpdateTasksWithDeletedTag}
        onCleanUpCompletedTasks={onCleanUpCompletedTasks}
        onSaveAllAndRefresh={onSaveAllAndRefresh}
        hasUnsavedChanges={hasUnsavedChanges}
        handleImportData={importDataHook}
        setShowModal={setShowModal}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        onAddSampleTasks={onAddSampleTasks} // NEW: Pass down to SettingsDrawer
      />
      <MainContent
        tasks={tasks}
        addTask={addTask}
        updateTask={updateTask}
        deleteTask={deleteTask}
        settings={settings}
        todoLists={todoLists}
        todoDispatch={todoDispatch}
        handleWipeAll={handleWipeAll}
        showModal={showModal}
        setShowModal={setShowModal}
        taskDispatch={taskDispatch}
        isDrawerOpen={isDrawerOpen}
        dailyTasks={dailyTasks}
        addDailyTask={addDailyTask}
      />
    </div>
  );
};

function App() {
  const {
    tasks, // This is currentTasks from the hook
    dailyTasks, // This is currentDailyTasks from the hook
    addTask,
    updateTask,
    deleteTask,
    taskDispatch,
    addDailyTask,
    saveTasksNow,
  } = useTaskManager();

  const {
    settings,
    toggleSetting,
    updateSetting,
    deleteTag,
    saveAllToFile,
    saveSettingsNow,
    importData,
    clearLastSavedSnapshot,
  } = useSettings();

  const [todoLists, todoDispatch] = useTodoLists();
  const { saveTodoListsNow } = useTodoLists();

  const [showModal, setShowModal] = useState(false);
  // Initialize isDrawerOpen based on settings, but ensure settings is defined.
  const [isDrawerOpen, setIsDrawerOpen] = useState(
    settings ? settings.keepDrawerOpen : false
  );

  const { hasUnsavedChanges, refreshChangeStatus } = useDataChangeDetector(
    tasks,
    todoLists,
    dailyTasks,
    settings
  );

  useEffect(() => {
    refreshChangeStatus();
  }, [tasks, todoLists, dailyTasks, settings, refreshChangeStatus]);

  useEffect(() => {
    // Ensure settings is defined before accessing keepDrawerOpen
    if (settings) {
      setIsDrawerOpen(settings.keepDrawerOpen);
    }
  }, [settings?.keepDrawerOpen]); // Optional chaining for dependency

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (settings.saveOnBrowserClose) {
        if (typeof saveSettingsNow === "function") saveSettingsNow();
        if (typeof saveTasksNow === "function") saveTasksNow();
        if (typeof saveTodoListsNow === "function") saveTodoListsNow();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    settings.saveOnBrowserClose,
    saveSettingsNow,
    saveTasksNow,
    saveTodoListsNow,
  ]);

  const handleWipeAll = () => {
    if (!showModal) return;
    try {
      // deleteTask(null); // This might not be what you intend for wiping all tasks.
      // Your reducer's DELETE_TASK with no ID clears the array.
      if (taskDispatch) {
        taskDispatch({ type: "SET_STATE", tasks: [], dailyTasks: [] });
      }
      if (todoDispatch) {
        // Ensure todoDispatch is available
        todoDispatch({ type: "SET_LISTS", lists: [] });
      }

      setShowModal(false);
      window.localStorage.removeItem("tasks");
      window.localStorage.removeItem("todoLists");
      window.localStorage.removeItem("dailyTasks");
      window.localStorage.removeItem("settings");
      if (typeof clearLastSavedSnapshot === "function")
        clearLastSavedSnapshot();
      refreshChangeStatus();
      window.location.reload();
    } catch (error) {
      console.error("Error during wipe all:", error);
      setShowModal(false);
      alert("Failed to wipe data. Please try again.");
    }
  };

  const handleSaveAllAndRefresh = () => {
    saveAllToFile(() => {
      refreshChangeStatus();
    });
  };

  const handleImportCompletion = () => {
    if (typeof clearLastSavedSnapshot === "function") clearLastSavedSnapshot();
    refreshChangeStatus();
  };

  const importDataHandler = useDataImport(
    taskDispatch,
    todoDispatch,
    (dataFromFile) => importData(dataFromFile, handleImportCompletion)
  );

  const handleUpdateTasksWithDeletedTag = useCallback(
    (deletedTag) => {
      if (taskDispatch) {
        taskDispatch({ type: "REMOVE_TAG_FROM_ALL_TASKS", tag: deletedTag });
        refreshChangeStatus();
      }
    },
    [taskDispatch, refreshChangeStatus]
  );

  const handleCleanUpCompletedTasks = useCallback(() => {
    if (taskDispatch) {
      taskDispatch({ type: "CLEAN_UP_COMPLETED_TASKS" });
      refreshChangeStatus();
    }
  }, [taskDispatch, refreshChangeStatus]);

  // NEW: Function to add sample tasks
  const handleAddSampleTasks = useCallback(() => {
    const createSampleTaskWithNewIdsRecursive = (template) => {
      const taskData = { ...template };
      delete taskData.children;

      if (template.name === "Set a Deadline & See Countdown") {
        taskData.deadline = new Date(
          Date.now() + 2 * 24 * 60 * 60 * 1000
        ).toISOString();
      }
      const newTask = createTask(taskData); // from taskUtils.js
      if (template.children && template.children.length > 0) {
        newTask.children = template.children.map((childTemplate) =>
          createSampleTaskWithNewIdsRecursive(childTemplate)
        );
      }
      return newTask;
    };

    const freshSampleTasks = SAMPLE_TASK_TEMPLATES.map((template) =>
      createSampleTaskWithNewIdsRecursive(template)
    );

    // Use taskDispatch from useTaskManager to update the state
    // tasks and dailyTasks are already in scope from useTaskManager()
    taskDispatch({
      type: "SET_STATE",
      tasks: [...tasks, ...freshSampleTasks], // Append to current tasks
      dailyTasks: dailyTasks, // Preserve current daily tasks
    });

    alert("Sample tasks have been added to your list!"); // Or use a more subtle notification
    refreshChangeStatus(); // Data has changed
  }, [tasks, dailyTasks, taskDispatch, refreshChangeStatus]); // Added dependencies

  // Ensure settings is loaded before rendering MainApplicationUI if it relies on it heavily for initial state
  if (!settings) {
    return <div>Loading settings...</div>; // Or some other loading indicator
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/"
          element={
            <MainApplicationUI
              tasks={tasks}
              dailyTasks={dailyTasks}
              addTask={addTask}
              updateTask={updateTask}
              deleteTask={deleteTask}
              taskDispatch={taskDispatch}
              addDailyTask={addDailyTask}
              settings={settings}
              toggleSetting={toggleSetting}
              updateSetting={updateSetting}
              deleteTag={deleteTag}
              onUpdateTasksWithDeletedTag={handleUpdateTasksWithDeletedTag}
              onCleanUpCompletedTasks={handleCleanUpCompletedTasks}
              onSaveAllAndRefresh={handleSaveAllAndRefresh}
              hasUnsavedChanges={hasUnsavedChanges}
              importDataHook={importDataHandler}
              todoLists={todoLists}
              todoDispatch={todoDispatch}
              showModal={showModal}
              setShowModal={setShowModal}
              handleWipeAll={handleWipeAll}
              isDrawerOpen={isDrawerOpen}
              setIsDrawerOpen={setIsDrawerOpen}
              onAddSampleTasks={handleAddSampleTasks} // NEW: Pass the function
            />
          }
        />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/accessibility-policy" element={<AccessibilityPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/documentation" element={<Documentation />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/faq" element={<FAQ />} />
      </Routes>
    </Router>
  );
}

export default App;
